import { collection, onSnapshot } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../backend/firebase';
import { FaChevronRight, FaChevronDown, FaCube, FaClock } from 'react-icons/fa';
import './DatasetRequests.css';

const DatasetRequests = () => {
  const { uid } = useParams();
  const [requestData, setRequestData] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) return;

    const reqRef = collection(db, 'Users', uid, 'requests');

    const unsubscribe = onSnapshot(
      reqRef,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRequestData(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching requests:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    };
  };

  // Calculate total requests across all versions for the header summary
  const getTotalRequests = (versions) => {
    if (!versions) return 0;
    return Object.values(versions).reduce((acc, curr) => acc + curr.length, 0);
  };

  return (
    <div className="dr-container">
      <div className="dr-wrapper">
        <header className="dr-page-header">
          <h1 className="dr-title">Access Logs</h1>
          <div className="dr-subtitle">
            User ID: <span className="mono">{uid}</span>
          </div>
        </header>

        {loading ? (
          <div className="dr-message">Syncing data...</div>
        ) : requestData.length === 0 ? (
          <div className="dr-message">No request history found.</div>
        ) : (
          <div className="dr-list">
            {requestData.map(req => {
              const isExpanded = expanded[req.id];
              const totalHits = getTotalRequests(req.versions);

              return (
                <div key={req.id} className="dr-item-group">
                  {/* Top Level: Dataset ID */}
                  <div 
                    className={`dr-row-header ${isExpanded ? 'active' : ''}`} 
                    onClick={() => toggleExpand(req.id)}
                  >
                    <div className="col-icon">
                      {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </div>
                    <div className="col-name mono">{req.id}</div>
                    <div className="col-meta">
                      {totalHits} {totalHits === 1 ? 'Request' : 'Requests'}
                    </div>
                    <div className="col-date">
                      Updated {req.lastUpdated ? formatDateTime(req.lastUpdated).date : 'N/A'}
                    </div>
                  </div>

                  {/* Expanded Level: Versions */}
                  {isExpanded && req.versions && (
                    <div className="dr-versions-container">
                      {Object.entries(req.versions).map(([version, timestamps]) => (
                        <div key={version} className="dr-version-block">
                          
                          <div className="dr-version-header">
                            <FaCube className="version-icon" />
                            <span className="mono bold">{version.replace(/_/g, '.')}</span>
                            <span className="version-count-badge">{timestamps.length} hits</span>
                          </div>

                          {/* Level 3: The Actual Logs */}
                          <div className="dr-logs-table">
                            <div className="log-row head">
                              <span className="w-time">TIMESTAMP</span>
                              <span className="w-status">STATUS</span>
                              <span className="w-method">METHOD</span>
                            </div>
                            
                            <div className="log-scroller">
                              {timestamps
                                .sort((a, b) => new Date(b) - new Date(a)) // Sort Newest First
                                .map((ts, idx) => {
                                  const { date, time } = formatDateTime(ts);
                                  return (
                                    <div key={idx} className="log-row body">
                                      <span className="w-time mono">
                                        <FaClock className="tiny-icon" /> 
                                        <span className="dim-date">{date}</span> 
                                        {time}
                                      </span>
                                      <span className="w-status success">
                                        <span className="dot"></span> 200 OK
                                      </span>
                                      <span className="w-method mono dim">GET</span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DatasetRequests;